import type {ReactNode} from "react";
import {useSelector} from "react-redux";

import {SEGMENT} from "@reva/shared/reva-spec";

import I18nErrorMessage from "../../component/I18nErrorMessage";
import LoadingIndicator from "../../component/LoadingIndicator";
import MissingDataIndicator from "../../component/MissingDataIndicator";
import MissingPermissionIndicator from "../../component/MissingPermissionIndicator";
import ValueLendingValueInfo from "../../component/ValueLendingValueInfo";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {selectEnergyIndicationData, selectEnergyRows} from "../../redux/energySelectors";
import {selectSegment} from "../../redux/objectParametersSlice";
import {selectPersistenceInitialized} from "../../redux/persistenceSlice";
import {selectSpec} from "../../redux/specSlice";
import {selectError, selectIndicationData, selectLoading} from "../../redux/valueIndicationSlice";
import {type EnergyOutputVariant, withEnergyPageForVariant} from "../../util/energySpecUtil";
import {aggregateIndicationTableData} from "../../util/indicationTableUtil";
import {RevaPermission} from "../../util/revaPermission";
import PersistenceFinalMessage from "../object/PersistenceFinalMessage";
import SnapshotMessage from "../object/SnapshotMessage";
import {EnergyScale} from "../object/TabObjectEnergy/EnergyScale";
import {getInfoText, IndicationTableWithData, InfoData} from "./IndicationTable";

// Single value column for the energy indication table — unlike the value/lending-value endpoints,
// ENERGY_VALUE rows only ever populate the "factor" cell (see reva_indication_rows.col_factor_*).
const ENERGY_COLUMNS = [{headerDe: "Wert", headerEn: "Value", field: "factor"}];

interface EnergyValueOutputProps {
    /**
     * - `preview`: Vorschau im Objekt-Tab „Energieausweis" — Energieskala, read-only Tabelle, Hinweise darunter
     * - `value`: Wert-Tab „Energiewert" — wie die Wertermittlungs-Tabs mit Markt-/Beleihungswert-Balken oben,
     *   editierbar (AVM-Schicht) und mit Hinweisen als Sidebar rechts; Energieskala über der Tabelle
     * Beide zeigen nur die Gruppen ihres Präfixes, siehe {@link withEnergyPageForVariant}.
     */
    variant: EnergyOutputVariant;
}

/**
 * Ausgabe der Energie-Indikation aus spec.pages.ENERGY_VALUE samt Hinweisblock (#19096).
 */
export function EnergyValueOutput({variant}: EnergyValueOutputProps): ReactNode {
    const indicationLoading = useSelector(selectLoading);
    const rowData = useSelector(selectEnergyRows);
    const energyData = useSelector(selectEnergyIndicationData);
    const indicationData = useSelector(selectIndicationData);
    const indicationError = useSelector(selectError);
    const segment = useSelector(selectSegment);
    const spec = useSelector(selectSpec);
    const {t, language} = useRevaTranslation();

    if (indicationLoading) return <LoadingIndicator loading={indicationLoading} />;

    // Seit #19381 gibt es nur noch eine Anfrage: ein Fehler der Wertindikation ist zugleich der Fehler
    // des Energiekennwerts.
    if (indicationError) {
        return (
            <>
                <I18nErrorMessage i18nKey={indicationError.error} />
                <I18nErrorMessage errorObject={indicationError} />
            </>
        );
    }

    if (!rowData || !energyData) {
        const emptyMessage = (
            <h3 className={"panelInputs"}>
                <div>{t("value.emptyMessage")}</div>
            </h3>
        );
        // Grundstücke haben kein Gebäude und damit regulär keine Energiekennwert-Indikation — dort ist
        // weder ein Berechtigungs- noch ein Fehlerhinweis angebracht.
        if (segment === SEGMENT.GRST_K) return emptyMessage;
        // Sonst reist der Energiekennwert als `energy_value_details` der Wertantwort mit und fehlt ohne das
        // Recht `indication-energy-value` stumm — dann gehört hier der Hinweis auf die Berechtigung hin,
        // nicht „kein Ergebnis". Eingefrorene Daten eines Snapshots sind an dieser Stelle schon vorbei: die
        // zeigt der Zweig darüber, unabhängig von den heutigen Rechten des Betrachters.
        // Liegt eine Wertantwort ohne Energieblock vor, ist die Teilindikation weich gescheitert; ihr Grund
        // steht im `info_hint` der Wertantwort, nicht in einem eigenen Fehler (AVM antwortet mit HTTP 200).
        return (
            <MissingPermissionIndicator permissionRule={RevaPermission.INDICATION_ENERGY_VALUE}>
                {indicationData ? (
                    <>
                        <h3 className={"panelInputs"}>
                            <div>{t("object.energy.output.indication_unavailable")}</div>
                        </h3>
                        <InfoData
                            endpoint="ENERGY_VALUE"
                            infoOutput={[]}
                            infoModelOutput={[]}
                            infoHintOutput={getInfoText(indicationData, language, "info_hint")}
                            className="rounded-border bg-surface-100 dark:bg-surface-700 p-4 mt-2 flex flex-col justify-between"
                        />
                    </>
                ) : (
                    emptyMessage
                )}
            </MissingPermissionIndicator>
        );
    }
    // EnergyScale is unrelated to the Reva Indication Spec (no reva_indication_rows entry, and
    // never will be) — it keeps reading its three values from the legacy regex-grouped rowData.
    const energy_value = rowData?.["energy_certificate"]?.["energy_value"];
    const energy_value_primary = rowData?.["energy_certificate"]?.["energy_value_primary"];
    const energy_type = rowData?.["energy_certificate"]?.["energy_type"];

    const rows = spec?.pages?.ENERGY_VALUE
        ? aggregateIndicationTableData(language, withEnergyPageForVariant(spec, variant), "ENERGY_VALUE", {
              input: {},
              output: energyData,
              combined: energyData,
          })
        : [];
    const initiallyExpandedGroups = Object.fromEntries(
        rows.map((row) => [row.group.groupKey, row.group.unfoldInitially]),
    );
    // Same info sections as the value-indication tabs (IndicationTable)
    const infoOutput = getInfoText(energyData, language, "info_info");
    const infoModelOutput = getInfoText(energyData, language, "info_model");
    const infoHintOutput = getInfoText(energyData, language, "info_hint");

    if (variant === "value") {
        // Layout wie IndicationTable; der Balken sorgt für dieselbe Starthöhe wie in den anderen Wert-Tabs. Die
        // Skala steht in der Tabellenspalte, damit die Sidebar oben bündig bleibt (#19096).
        const noSideBar = !infoOutput.length && !infoModelOutput.length;
        return (
            <>
                <ValueLendingValueInfo />
                <div className="grid grid-cols-12 gap-4 mx-0">
                    <div className="col-span-12 xl:col-span-9 pl-0">
                        <EnergyScale
                            energy_value={energy_value}
                            energy_value_primary={energy_value_primary}
                            energy_type={energy_type}
                        />
                        <IndicationTableWithData
                            rows={rows}
                            specColumns={ENERGY_COLUMNS}
                            fullsize={noSideBar}
                            endpoint="ENERGY_VALUE"
                            initiallyExpandedGroups={initiallyExpandedGroups}
                        />
                    </div>
                    <InfoData
                        endpoint="ENERGY_VALUE"
                        infoOutput={infoOutput}
                        infoModelOutput={infoModelOutput}
                        infoHintOutput={infoHintOutput}
                    />
                </div>
            </>
        );
    }

    return (
        <>
            <EnergyScale
                energy_value={energy_value}
                energy_value_primary={energy_value_primary}
                energy_type={energy_type}
            />
            <IndicationTableWithData
                rows={rows}
                specColumns={ENERGY_COLUMNS}
                fullsize
                endpoint="ENERGY_VALUE"
                initiallyExpandedGroups={initiallyExpandedGroups}
                editable={false}
            />
            <InfoData
                endpoint="ENERGY_VALUE"
                infoOutput={infoOutput}
                infoModelOutput={infoModelOutput}
                infoHintOutput={infoHintOutput}
                className="rounded-border bg-surface-100 dark:bg-surface-700 p-4 mt-2 flex flex-col justify-between"
            />
        </>
    );
}

/**
 * Wert-Tab „Energiewert": die Energie-Indikation analog zu den Wertermittlungs-Tabs, ohne Würdigung (#19096).
 */
export default function TabEnergyValueIndication(): ReactNode {
    const energyData = useSelector(selectEnergyIndicationData);
    const loading = useSelector(selectLoading);
    const persistenceInitialized = useSelector(selectPersistenceInitialized);

    return (
        <MissingDataIndicator dataAvailable={!!energyData || loading || !persistenceInitialized}>
            <PersistenceFinalMessage />
            <SnapshotMessage />
            <EnergyValueOutput variant="value" />
        </MissingDataIndicator>
    );
}
